// frontend/src/pages/SettingsPage/components/SettingsIntegrationCategoryContent.tsx
import { ArrowLeft } from "lucide-react";
import React, { Suspense, useEffect, useRef, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { Navigate, useLocation, useNavigate } from "react-router";
import {
  getIntegrationCategories,
  INTEGRATION_INFO,
  IntegrationCategory,
  IntegrationType,
} from "src/components/integrations/integration-categories";
import IntegrationCard from "src/components/integrations/IntegrationCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import { LoadingSpinner } from "src/components/ui/loading-spinner";
import { usePermissions } from "src/context/MeContext";
import { useToast } from "src/hooks/use-toast";
import AwsIntegrationsDialogLazy from "src/pages/SettingsPage/components/AwsIntegrationsDialog-lazy";
import BitbucketIntegration from "src/pages/SettingsPage/components/BitbucketIntegration";
import BitbucketIntegrationsDialog from "src/pages/SettingsPage/components/BitbucketIntegrationsDialog";
import BuildkiteIntegration from "src/pages/SettingsPage/components/BuildkiteIntegration";
import CloudWatchIntegrationLazy from "src/pages/SettingsPage/components/CloudWatchIntegration-lazy";
import CloudWatchIntegrationsDialogLazy from "src/pages/SettingsPage/components/CloudWatchIntegrationsDialog-lazy";
import ConfluenceIntegrationLazy from "src/pages/SettingsPage/components/ConfluenceIntegration-lazy";
import CursorIntegration from "src/pages/SettingsPage/components/CursorIntegration";
import DatadogIntegration from "src/pages/SettingsPage/components/DatadogIntegration";
import DatadogIntegrationsDialog from "src/pages/SettingsPage/components/DatadogIntegrationsDialog";
import DevinIntegration from "src/pages/SettingsPage/components/DevinIntegration";
import DiscordIntegrationLazy from "src/pages/SettingsPage/components/DiscordIntegration-lazy";
import ElasticsearchIntegrationLazy from "src/pages/SettingsPage/components/ElasticsearchIntegration-lazy";
import FireHydrantIntegrationLazy from "src/pages/SettingsPage/components/FireHydrantIntegration-lazy";
import GcpCloudMonitoringIntegration from "src/pages/SettingsPage/components/GcpCloudMonitoringIntegration";
import GcpIntegrationLazy from "src/pages/SettingsPage/components/GcpIntegration-lazy";
import GithubIntegration from "src/pages/SettingsPage/components/GithubIntegration";
import GitHubIntegrationsDialog from "src/pages/SettingsPage/components/GitHubIntegrationsDialog";
import GitLabIntegration from "src/pages/SettingsPage/components/GitLabIntegration";
import GitLabIntegrationsDialog from "src/pages/SettingsPage/components/GitLabIntegrationsDialog";
import GrafanaCloudIntegration from "src/pages/SettingsPage/components/GrafanaCloudIntegration";
import IncidentIoIntegration from "src/pages/SettingsPage/components/IncidentIoIntegration";
import { IntegrationCredentialsDialog } from "src/pages/SettingsPage/components/IntegrationsSection";
import JiraIntegrationLazy from "src/pages/SettingsPage/components/JiraIntegration-lazy";
import LangfuseIntegrationLazy from "src/pages/SettingsPage/components/LangfuseIntegration-lazy";
import LangSmithIntegrationLazy from "src/pages/SettingsPage/components/LangSmithIntegration-lazy";
import LinearIntegrationLazy from "src/pages/SettingsPage/components/LinearIntegration-lazy";
import NewRelicIntegration from "src/pages/SettingsPage/components/NewRelicIntegration";
import NotionIntegrationLazy from "src/pages/SettingsPage/components/NotionIntegration-lazy";
import OpsgenieIntegrationLazy from "src/pages/SettingsPage/components/OpsgenieIntegration-lazy";
import PagerDutyIntegrationLazy from "src/pages/SettingsPage/components/PagerDutyIntegration-lazy";
import PGAnalyzeIntegration from "src/pages/SettingsPage/components/PGAnalyzeIntegration";
import PostgreSQLIntegrationLazy from "src/pages/SettingsPage/components/PostgreSQLIntegration-lazy";
import PostgreSQLIntegrationsDialog from "src/pages/SettingsPage/components/PostgreSQLIntegrationsDialog";
import PrometheusIntegrationLazy from "src/pages/SettingsPage/components/PrometheusIntegration-lazy";
import RollbarIntegrationLazy from "src/pages/SettingsPage/components/RollbarIntegration-lazy";
import RollbarIntegrationsDialog from "src/pages/SettingsPage/components/RollbarIntegrationsDialog";
import RootlyIntegrationLazy from "src/pages/SettingsPage/components/RootlyIntegration-lazy";
import SentryIntegrationLazy from "src/pages/SettingsPage/components/SentryIntegration-lazy";
import SlackIntegration from "src/pages/SettingsPage/components/SlackIntegration";
import SnowflakeIntegrationLazy from "src/pages/SettingsPage/components/SnowflakeIntegration-lazy";
import SocketIntegrationLazy from "src/pages/SettingsPage/components/SocketIntegration-lazy";
import SocketIntegrationsDialogLazy from "src/pages/SettingsPage/components/SocketIntegrationsDialog-lazy";
import TemporalIntegration from "src/pages/SettingsPage/components/TemporalIntegration";
import { IntegrationProvider } from "src/pages/SettingsPage/context/IntegrationContext";
import { getProductName, getSupportEmail } from "src/utils/brandTheme";

import { SettingsIntegrationCategoryContent_organization$key } from "./__generated__/SettingsIntegrationCategoryContent_organization.graphql";

const settingsIntegrationCategoryContentFragment = graphql`
  fragment SettingsIntegrationCategoryContent_organization on GraphQLOrganization {
    brandTheme
    slackbotInviteLink
    autoRespondInThreads
    privateChannelsEnabled
    respondToBotMentions
    hasPendingGithubInstallation
    integrations {
      edges {
        node {
          id
          integrationType
        }
      }
    }
  }
`;

interface SettingsIntegrationCategoryContentProps {
  category: IntegrationCategory;
  organization: SettingsIntegrationCategoryContent_organization$key;
  onRefetch: () => void;
}

export default function SettingsIntegrationCategoryContent({
  category,
  organization: organizationKey,
  onRefetch,
}: SettingsIntegrationCategoryContentProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { isSuperAdmin } = usePermissions();
  const organization = useFragment(settingsIntegrationCategoryContentFragment, organizationKey);

  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationType | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const brandTheme = organization.brandTheme;
  const slackbotInviteLink = organization.slackbotInviteLink;

  // Track if we've already shown setup toasts to prevent duplicates
  const hasShownSetupToastRef = useRef(false);

  // Auto-open a specific integration dialog when ?integration=<TYPE> is present in the URL
  const hasAutoOpenedDialogRef = useRef(false);
  useEffect(() => {
    if (hasAutoOpenedDialogRef.current) return;
    const urlParams = new URLSearchParams(location.search);
    const integrationParam = urlParams.get("integration");
    if (!integrationParam) return;
    const type = integrationParam.toUpperCase() as IntegrationType;
    if (!INTEGRATION_INFO[type]) return;
    if (!category.integrationTypes.includes(type)) return;
    hasAutoOpenedDialogRef.current = true;
    setSelectedIntegration(type);
    setDialogOpen(true);
    urlParams.delete("integration");
    const remaining = urlParams.toString();
    navigate(`${location.pathname}${remaining ? `?${remaining}` : ""}`, { replace: true });
  }, [location.search, location.pathname, navigate, category.integrationTypes]);

  // Handle GitHub/GitLab setup query params for toast notifications
  useEffect(() => {
    if (hasShownSetupToastRef.current) return;

    const urlParams = new URLSearchParams(location.search);
    const githubSetup = urlParams.get("github_setup");
    const gitlabSetup = urlParams.get("gitlab_setup");
    const bitbucketSetup = urlParams.get("bitbucket_setup");
    const linearAgentSetup = urlParams.get("linear_agent_setup");
    const error = urlParams.get("error");

    if (!githubSetup && !gitlabSetup && !bitbucketSetup && !linearAgentSetup) return;

    hasShownSetupToastRef.current = true;

    // Small delay to ensure Toaster component has mounted and registered its listener
    const timeoutId = setTimeout(() => {
      if (githubSetup) {
        switch (githubSetup) {
          case "success":
            toast({
              title: "GitHub Connected",
              description: "Your GitHub integration has been set up successfully.",
            });
            onRefetch();
            break;
          case "pending":
            toast({
              title: "Installation Pending",
              description: "Your GitHub App installation request is pending admin approval.",
              variant: "default",
            });
            break;
          case "error": {
            let errorMessage: string;
            if (error === "missing_code") {
              errorMessage = "Missing authorization code from GitHub.";
            } else if (error === "missing_installation") {
              errorMessage = "Missing installation ID from GitHub.";
            } else if (error === "user_has_other_org") {
              errorMessage = "Your GitHub account is already linked to another organization.";
            } else if (error === "user_already_pending") {
              errorMessage = "You already have a pending installation for another organization.";
            } else if (error === "unexpected_action") {
              errorMessage = "Unexpected response from GitHub.";
            } else if (error === "no_installation_request") {
              errorMessage = "Could not find your installation request. Please try again.";
            } else if (error === "oauth_not_configured") {
              errorMessage = "GitHub OAuth is not configured. Please contact support.";
            } else if (error === "oauth_exchange_failed") {
              errorMessage = "Failed to verify your GitHub identity. Please try again.";
            } else if (error) {
              errorMessage = error;
            } else {
              errorMessage = "An error occurred while setting up GitHub integration.";
            }
            toast({
              title: "GitHub Setup Failed",
              description: errorMessage,
              variant: "error",
            });
            break;
          }
          case "manual":
            toast({
              title: "Manual Setup Required",
              description: "Please contact support to complete your GitHub integration setup.",
              variant: "default",
            });
            break;
        }
      }

      if (gitlabSetup) {
        switch (gitlabSetup) {
          case "success":
            toast({
              title: "GitLab Connected",
              description: "Your GitLab integration has been set up successfully.",
            });
            onRefetch();
            break;
          case "error": {
            let errorMessage: string;
            if (error === "missing_params") {
              errorMessage = "Missing authorization code or state from GitLab.";
            } else if (error === "invalid_state") {
              errorMessage = "Invalid OAuth state. Please try again.";
            } else if (error === "missing_credentials") {
              errorMessage = "Missing OAuth credentials for your GitLab instance.";
            } else if (error === "oauth_failed") {
              errorMessage = "Failed to complete GitLab OAuth flow. Please try again.";
            } else if (error) {
              errorMessage = error;
            } else {
              errorMessage = "An error occurred while setting up GitLab integration.";
            }
            toast({
              title: "GitLab Setup Failed",
              description: errorMessage,
              variant: "error",
            });
            break;
          }
        }
      }

      if (bitbucketSetup) {
        switch (bitbucketSetup) {
          case "success":
            toast({
              title: "Bitbucket Connected",
              description: "Your Bitbucket integration has been set up successfully.",
            });
            onRefetch();
            break;
          case "error": {
            let errorMessage: string;
            if (error === "missing_params") {
              errorMessage = "Missing authorization code or state from Bitbucket.";
            } else if (error === "invalid_state") {
              errorMessage = "Invalid OAuth state. Please try again.";
            } else if (error === "missing_credentials") {
              errorMessage = "Missing OAuth credentials for Bitbucket.";
            } else if (error === "oauth_failed") {
              errorMessage = "Failed to complete Bitbucket OAuth flow. Please try again.";
            } else if (error) {
              errorMessage = error;
            } else {
              errorMessage = "An error occurred while setting up Bitbucket integration.";
            }
            toast({
              title: "Bitbucket Setup Failed",
              description: errorMessage,
              variant: "error",
            });
            break;
          }
        }
      }

      if (linearAgentSetup) {
        switch (linearAgentSetup) {
          case "success":
            toast({
              title: "Linear Agent Installed",
              description: "Your Linear agent has been installed successfully.",
            });
            onRefetch();
            break;
          case "error": {
            let errorMessage: string;
            if (error === "missing_params") {
              errorMessage = "Missing authorization code or state from Linear.";
            } else if (error === "invalid_state") {
              errorMessage = "Invalid OAuth state. Please try again.";
            } else if (error === "feature_not_enabled") {
              errorMessage = "Linear agent feature is not enabled for your organization.";
            } else if (error === "oauth_failed") {
              errorMessage = "Failed to complete Linear agent OAuth flow. Please try again.";
            } else if (error) {
              errorMessage = error;
            } else {
              errorMessage = "An error occurred while installing the Linear agent.";
            }
            toast({
              title: "Linear Agent Setup Failed",
              description: errorMessage,
              variant: "error",
            });
            break;
          }
        }
      }

      // Clear the query params from URL AFTER showing toast
      navigate(location.pathname, { replace: true });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [location.search, navigate, toast, onRefetch]);

  // Get the dynamic category based on brand theme (for correct title and filtered integrations)
  const productName = getProductName(brandTheme);
  const dynamicCategories = getIntegrationCategories(productName, brandTheme, isSuperAdmin);
  const dynamicCategory = dynamicCategories.find(cat => cat.id === category.id);

  // If this category has no visible integrations for the current user, redirect back
  if (!dynamicCategory) {
    return <Navigate to="/settings/integrations" replace />;
  }

  // Build a map of integration types to their IDs and a set of connected types
  const integrationMap = new Map<string, string>();
  (organization.integrations?.edges ?? []).forEach(edge => {
    if (edge?.node?.integrationType && edge?.node?.id) {
      integrationMap.set(edge.node.integrationType, edge.node.id);
    }
  });
  const connectedTypes = new Set(integrationMap.keys());

  // Helper to get integration object for generic dialog
  const getIntegrationForDialog = (type: IntegrationType) => ({
    id: integrationMap.get(type),
    name: INTEGRATION_INFO[type]?.name || type,
    type,
    isConnected: connectedTypes.has(type),
  });

  const handleBack = () => {
    navigate("/settings/integrations");
  };

  const handleIntegrationClick = (type: IntegrationType) => {
    setSelectedIntegration(type);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedIntegration(null);
  };

  const handleRefetch = () => {
    onRefetch();
    handleDialogClose();
  };

  const isConnected = (type: IntegrationType) => connectedTypes.has(type);
  const isAvailable = (type: IntegrationType) => INTEGRATION_INFO[type]?.available !== false;

  // Check if GitHub has a pending installation (no connected + hasPendingGithubInstallation)
  const hasGitHubConnected = connectedTypes.has("GITHUB");
  const hasPendingGitHub = !hasGitHubConnected && organization.hasPendingGithubInstallation;
  const isPending = (type: IntegrationType) => type === "GITHUB" && hasPendingGitHub;

  // Sort integrations: Connected (alpha) -> Not Connected (alpha) -> Not Available (alpha)
  const sortedIntegrationTypes = [...dynamicCategory.integrationTypes].sort((a, b) => {
    const aConnected = isConnected(a);
    const bConnected = isConnected(b);
    const aAvailable = isAvailable(a);
    const bAvailable = isAvailable(b);
    const aName = INTEGRATION_INFO[a]?.name || a;
    const bName = INTEGRATION_INFO[b]?.name || b;

    // Connected integrations come first
    if (aConnected && !bConnected) return -1;
    if (!aConnected && bConnected) return 1;

    // If both connected or both not connected, check availability
    if (!aConnected && !bConnected) {
      // Available (not connected) comes before unavailable
      if (aAvailable && !bAvailable) return -1;
      if (!aAvailable && bAvailable) return 1;
    }

    // Within same category, sort alphabetically by name
    return aName.localeCompare(bName);
  });

  const renderIntegrationDialog = () => {
    if (!dialogOpen || !selectedIntegration) return null;

    const dialogProps = {
      isOpen: dialogOpen,
      onClose: handleDialogClose,
      onRefetch: handleRefetch,
      onIntegrationChange: onRefetch,
      brandTheme,
    };

    const loadingFallback = (
      <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-4xl bg-popover text-foreground border-border">
          <DialogHeader>
            <DialogTitle>Loading...</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center p-8">
            <LoadingSpinner className="h-6 w-6" />
          </div>
        </DialogContent>
      </Dialog>
    );

    const integrationDialogs: Record<IntegrationType, React.ReactNode> = {
      DATADOG: isConnected("DATADOG")
        ? <DatadogIntegrationsDialog {...dialogProps} />
        : <DatadogIntegration {...dialogProps} />,
      NEW_RELIC: isConnected("NEW_RELIC")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("NEW_RELIC")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : <NewRelicIntegration {...dialogProps} />,
      GRAFANA_CLOUD: isConnected("GRAFANA_CLOUD")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("GRAFANA_CLOUD")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : <GrafanaCloudIntegration {...dialogProps} />,
      GCP_CLOUD_MONITORING: isConnected("GCP_CLOUD_MONITORING")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("GCP_CLOUD_MONITORING")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : <GcpCloudMonitoringIntegration {...dialogProps} />,
      SENTRY: isConnected("SENTRY")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("SENTRY")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : (
          <Suspense fallback={loadingFallback}>
            <SentryIntegrationLazy {...dialogProps} />
          </Suspense>
        ),
      ROLLBAR: isConnected("ROLLBAR")
        ? (
          <Suspense fallback={loadingFallback}>
            <RollbarIntegrationsDialog {...dialogProps} />
          </Suspense>
        )
        : (
          <Suspense fallback={loadingFallback}>
            <RollbarIntegrationLazy {...dialogProps} />
          </Suspense>
        ),
      ELASTICSEARCH: isConnected("ELASTICSEARCH")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("ELASTICSEARCH")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : (
          <Suspense fallback={loadingFallback}>
            <ElasticsearchIntegrationLazy {...dialogProps} />
          </Suspense>
        ),
      LANGSMITH: isConnected("LANGSMITH")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("LANGSMITH")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : (
          <Suspense fallback={loadingFallback}>
            <LangSmithIntegrationLazy {...dialogProps} />
          </Suspense>
        ),
      LANGFUSE: isConnected("LANGFUSE")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("LANGFUSE")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : (
          <Suspense fallback={loadingFallback}>
            <LangfuseIntegrationLazy {...dialogProps} />
          </Suspense>
        ),
      CLOUDWATCH: isConnected("CLOUDWATCH")
        ? (
          <Suspense fallback={loadingFallback}>
            <CloudWatchIntegrationsDialogLazy {...dialogProps} />
          </Suspense>
        )
        : (
          <Suspense fallback={loadingFallback}>
            <CloudWatchIntegrationLazy {...dialogProps} />
          </Suspense>
        ),
      CHRONOSPHERE: (
        <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
          <DialogContent className="sm:max-w-md bg-popover text-foreground border-border">
            <DialogHeader>
              <DialogTitle>Chronosphere</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              The Chronosphere integration is in early preview. Please contact{" "}
              <a href={`mailto:${getSupportEmail(brandTheme)}`} className="text-primary hover:underline">
                {getSupportEmail(brandTheme)}
              </a>{" "}
              to get access.
            </p>
          </DialogContent>
        </Dialog>
      ),
      // Unavailable integrations - no dialogs needed
      APPDYNAMICS: null,
      DYNATRACE: null,
      OPENSEARCH: null,
      PROMETHEUS: isConnected("PROMETHEUS")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("PROMETHEUS")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : (
          <Suspense fallback={loadingFallback}>
            <PrometheusIntegrationLazy {...dialogProps} />
          </Suspense>
        ),
      SPLUNK: null,
      BITBUCKET: isConnected("BITBUCKET")
        ? <BitbucketIntegrationsDialog {...dialogProps} />
        : <BitbucketIntegration {...dialogProps} />,
      GITHUB: isConnected("GITHUB")
        ? <GitHubIntegrationsDialog {...dialogProps} />
        : <GithubIntegration {...dialogProps} />,
      GITLAB: isConnected("GITLAB")
        ? <GitLabIntegrationsDialog {...dialogProps} />
        : <GitLabIntegration {...dialogProps} />,
      SLACK: <SlackIntegration {...dialogProps} slackbotInviteLink={slackbotInviteLink} isConnected={isConnected("SLACK")} autoRespondInThreads={organization.autoRespondInThreads} privateChannelsEnabled={organization.privateChannelsEnabled} respondToBotMentions={organization.respondToBotMentions} />,
      DISCORD: (
        <Suspense fallback={loadingFallback}>
          <DiscordIntegrationLazy {...dialogProps} />
        </Suspense>
      ),
      PAGERDUTY: (
        <Suspense fallback={loadingFallback}>
          <PagerDutyIntegrationLazy {...dialogProps} />
        </Suspense>
      ),
      OPSGENIE: (
        <Suspense fallback={loadingFallback}>
          <OpsgenieIntegrationLazy {...dialogProps} />
        </Suspense>
      ),
      ROOTLY: (
        <Suspense fallback={loadingFallback}>
          <RootlyIntegrationLazy {...dialogProps} />
        </Suspense>
      ),
      FIREHYDRANT: (
        <Suspense fallback={loadingFallback}>
          <FireHydrantIntegrationLazy {...dialogProps} />
        </Suspense>
      ),
      INCIDENT_IO: <IncidentIoIntegration {...dialogProps} />,
      NOTION: isConnected("NOTION")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("NOTION")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : (
          <Suspense fallback={loadingFallback}>
            <NotionIntegrationLazy {...dialogProps} />
          </Suspense>
        ),
      CONFLUENCE: isConnected("CONFLUENCE")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("CONFLUENCE")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : (
          <Suspense fallback={loadingFallback}>
            <ConfluenceIntegrationLazy {...dialogProps} />
          </Suspense>
        ),
      JIRA: isConnected("JIRA")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("JIRA")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : (
          <Suspense fallback={loadingFallback}>
            <JiraIntegrationLazy {...dialogProps} />
          </Suspense>
        ),
      LINEAR: (
        <Suspense fallback={loadingFallback}>
          <LinearIntegrationLazy {...dialogProps} />
        </Suspense>
      ),
      TEMPORAL: isConnected("TEMPORAL")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("TEMPORAL")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : <TemporalIntegration {...dialogProps} />,
      BUILDKITE: isConnected("BUILDKITE")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("BUILDKITE")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : <BuildkiteIntegration {...dialogProps} />,
      AWS: (
        <Suspense fallback={loadingFallback}>
          <AwsIntegrationsDialogLazy {...dialogProps} />
        </Suspense>
      ),
      GCP: (
        <Suspense fallback={loadingFallback}>
          <GcpIntegrationLazy {...dialogProps} />
        </Suspense>
      ),
      SNOWFLAKE: (
        <Suspense fallback={loadingFallback}>
          <SnowflakeIntegrationLazy {...dialogProps} />
        </Suspense>
      ),
      PGANALYZE: <PGAnalyzeIntegration {...dialogProps} />,
      SOCKET: isConnected("SOCKET")
        ? <SocketIntegrationsDialogLazy {...dialogProps} />
        : <SocketIntegrationLazy {...dialogProps} />,
      POSTGRESQL: isConnected("POSTGRESQL")
        ? (
          <Suspense fallback={loadingFallback}>
            <PostgreSQLIntegrationsDialog {...dialogProps} />
          </Suspense>
        )
        : (
          <Suspense fallback={loadingFallback}>
            <PostgreSQLIntegrationLazy {...dialogProps} />
          </Suspense>
        ),
      CURSOR: isConnected("CURSOR")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("CURSOR")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : <CursorIntegration {...dialogProps} />,
      DEVIN: isConnected("DEVIN")
        ? (
          <IntegrationCredentialsDialog
            integration={getIntegrationForDialog("DEVIN")}
            isOpen={dialogOpen}
            onClose={handleDialogClose}
            brandTheme={brandTheme}
          />
        )
        : <DevinIntegration {...dialogProps} />,
    };

    return (
      <IntegrationProvider>
        {integrationDialogs[selectedIntegration]}
      </IntegrationProvider>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 md:px-5 pt-4 pb-4">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Return to Integrations
        </button>

        <h1 className="text-xl font-semibold text-foreground">
          {dynamicCategory.title} Integrations
        </h1>
      </div>

      {/* Content */}
      <div className="flex-grow overflow-auto custom-scrollbar px-4 md:px-5 py-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl">
          {sortedIntegrationTypes.map(type => {
            // Hide manage button for Rootly integration in Rootly branded orgs
            const isRootlyBranded = productName === "Rootly AI SRE";
            const hideManage = isRootlyBranded && type === "ROOTLY";
            return (
              <IntegrationCard
                key={type}
                integrationType={type}
                isConnected={isConnected(type)}
                isPending={isPending(type)}
                onClick={() => handleIntegrationClick(type)}
                hideManage={hideManage}
              />
            );
          })}
        </div>
      </div>

      {renderIntegrationDialog()}
    </div>
  );
}
